import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getAllStudents } from "../../services/studentsService";
import { getCurrentUser } from "../../auth";
import "./StudentEnrollmentForm.css";
import { db } from "../../services/firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";

export default function StudentEnrollmentForm() {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const formRef = useRef(null);
  const currentUser = getCurrentUser();
  const role = String(currentUser?.role || "").toLowerCase();
  const userCampus = String(currentUser?.campus || "").toLowerCase();
  const isAdmin = role === "admin" || role === "superadmin" || userCampus === "all";
  const allStudents = useMemo(() => getAllStudents(), []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      const local = allStudents.find(s => String(s.id) === String(id));
      if (local && !cancelled) {
        setStudent(local);
        setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "students", String(id)));
        if (snap.exists() && !cancelled) {
          const data = { id: snap.id, ...snap.data() };
          if (!isAdmin && String(data.campus || "").toLowerCase() !== userCampus) {
            setNotFound(true); setLoading(false); return;
          }
          setStudent(data);
          setLoading(false);
          return;
        }
      } catch (_e) { void 0; }
      try {
        const q = query(collection(db, "students"), where("id", "==", String(id)));
        const qs = await getDocs(q);
        if (!cancelled && !qs.empty) {
          const d = qs.docs[0];
          const data = { id: d.id, ...d.data() };
          if (!isAdmin && String(data.campus || "").toLowerCase() !== userCampus) {
            setNotFound(true); setLoading(false); return;
          }
          setStudent(data);
          setLoading(false);
          return;
        }
      } catch (_e) { void 0; }
      if (!cancelled) {
        setStudent(null);
        setNotFound(true);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, allStudents]);

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="alert alert-info">Loading enrollment form…</div>
        <Link to="/sis/students" className="btn btn-secondary">Back to Students</Link>
      </div>
    );
  }

  if (!student && notFound) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">Student not found.</div>
        <Link to="/sis/students" className="btn btn-secondary">Back to Students</Link>
      </div>
    );
  }

  const schoolYear = (() => {
    const y = Number(student.enrollmentYear || new Date().getFullYear());
    return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  })();
  const fullNameFallback = String(student.fullname || student.fullName || student.name || "").trim();
  const names = {
    last: student.lastName || student.lastname || student.last_name || (fullNameFallback ? fullNameFallback.split(/\s+/).slice(-1)[0] : ""),
    first: student.firstName || student.firstname || student.first_name || (fullNameFallback ? fullNameFallback.split(/\s+/)[0] : ""),
    middle: student.middleName || student.middlename || student.middle_name || "",
    ext: ""
  };

  const splitName = (s) => {
    const t = String(s || "").trim().split(/\s+/);
    if (t.length === 0) return { first: "", middle: "", last: "" };
    if (t.length === 1) return { first: t[0], middle: "", last: "" };
    if (t.length === 2) return { first: t[0], middle: "", last: t[1] };
    return { first: t[0], middle: t.slice(1, -1).join(" "), last: t[t.length - 1] };
  };
  const parentParts = {
    last: student.fatherLast || splitName(student.parentName || "").last,
    first: student.fatherFirst || splitName(student.parentName || "").first,
    middle: student.fatherMiddle || splitName(student.parentName || "").middle
  };
  const motherParts = {
    last: student.motherMaidenLast || "",
    first: student.motherFirst || "",
    middle: student.motherMiddle || ""
  };
  const guardianParts = splitName(student.guardianName || "");
  const hasLRN = Boolean(student.lrn);
  const isReturning = Boolean(student.returning || student.isReturning || student.balikAral);
  const sex = String(student.gender || student.sex || "").toLowerCase();
  const shsSemester = String(student.semester || student.sem || "").toLowerCase();
  const dlSelected = new Set(
    Array.isArray(student.distanceLearning) ? student.distanceLearning.map(s => String(s).toLowerCase()) :
    Array.isArray(student.modalities) ? student.modalities.map(s => String(s).toLowerCase()) : []
  );

  function handlePrint(mode = "two") {
    const el = formRef.current;
    if (!el) return window.print();
    const cssCommon = `
      @page { size: 8.5in 13in; margin: 0.5in; }
      html, body { height: auto; }
      body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #000; margin: 0; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }

      .print-page { width: 100%; max-width: 7.5in; margin: 0 auto; }
      .card { background: #fff; }
      .p-3 { padding: 16px; }

      .row { display: flex; flex-wrap: wrap; margin-left: -4px; margin-right: -4px; }
      .row > * { padding-left: 4px; padding-right: 4px; }
      .row.g-2 > * { margin-top: 8px; }
      .col-12 { flex: 0 0 100%; max-width: 100%; }
      .col-md-1 { flex: 0 0 8.333333%; max-width: 8.333333%; }
      .col-md-2 { flex: 0 0 16.666667%; max-width: 16.666667%; }
      .col-md-3 { flex: 0 0 25%; max-width: 25%; }
      .col-md-4 { flex: 0 0 33.333333%; max-width: 33.333333%; }
      .col-md-6 { flex: 0 0 50%; max-width: 50%; }
      .col-md-12 { flex: 0 0 100%; max-width: 100%; }

      .d-flex { display: flex; }
      .align-items-center { align-items: center; }
      .justify-content-between { justify-content: space-between; }
      .flex-wrap { flex-wrap: wrap; }
      .gap-3 { gap: 12px; }
      .fw-bold { font-weight: 700; }
      .text-muted { color: #666; }
      .small { font-size: 10.5px; }
      .mb-0 { margin-bottom: 0; }
      .mb-1 { margin-bottom: 4px; }
      .mb-3 { margin-bottom: 10px; }
      .mt-2 { margin-top: 8px; }
      .mt-3 { margin-top: 10px; }
      .mt-4 { margin-top: 14px; }
      .ms-3 { margin-left: 12px; }

      .form-label { display: block; font-weight: 700; margin: 0 0 2px; }
      .depEd-header { display: grid; grid-template-columns: 120px 1fr 120px; align-items: center; gap: 8px; }
      .depEd-header__left img { height: 72px; width: 72px; object-fit: contain; }
      .depEd-header__title { text-align: center; font-weight: 800; }
      .depEd-header__subtitle { text-align: center; font-size: 12px; font-weight: 700; }
      .section-title { font-weight: 700; border-top: 2px solid #333; padding-top: 4px; margin-top: 8px; }
      .boxed { border: 1px solid #333; min-height: 32px; padding: 6px 8px; background: #fff; display: block; }
      .checkline .box { display: inline-block; width: 16px; height: 16px; border: 1px solid #333; margin-right: 6px; vertical-align: middle; }
      .checkline { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
      .checkline .boxed { display: inline-block; min-width: 140px; }
      .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }

      .avoid-break { break-inside: avoid; page-break-inside: avoid; }
      .page-break { break-after: page; page-break-after: always; height: 0; }

      .student-photo { width: 80px !important; height: 80px !important; }
      table, th, td { color: #000; }
    `;
    const cssTwo = `
      body { font-size: 11px; }
    `;
    const cssOne = `
      @page { margin: 0.5in; }
      body { font-size: 10px; zoom: 0.9; }
      .row { margin-left: -3px; margin-right: -3px; }
      .row > * { padding-left: 3px; padding-right: 3px; }
      .row.g-2 > * { margin-top: 6px; }
      .section-title { border-top: 1px solid #333; padding-top: 3px; margin-top: 6px; }
      .boxed { min-height: 18px; padding: 3px 5px; }
      .small { font-size: 9.5px; }
      .grid { grid-template-columns: repeat(4, 1fr); gap: 4px; }
      .student-photo { width: 72px !important; height: 72px !important; }
    `;

    const wrapper = document.createElement("div");
    wrapper.innerHTML = el.innerHTML;
    if (mode === "one") {
      wrapper.querySelectorAll(".page-break").forEach(n => n.remove());
    }
    const css = `${cssCommon}\n${mode === "one" ? cssOne : cssTwo}`;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Enrollment Form</title><style>${css}</style></head><body><div class="print-page"><div class="card p-3">${wrapper.innerHTML}</div></div></body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.onload = () => { try { w.focus(); w.print(); w.close(); } catch (e) { setTimeout(() => { try { w.focus(); w.print(); w.close(); } catch (err) { void 0; } }, 300); } };
  }

  return (
    <div className="container mt-4 deped-form">
      <div className="no-print d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Basic Education Enrollment Form</h4>
        <div>
          <button className="btn btn-primary me-2" onClick={() => handlePrint("one")}>Print 1 Page (8.5×13)</button>
          <button className="btn btn-outline-primary me-2" onClick={() => handlePrint("two")}>Print 2 Pages (8.5×13)</button>
          <button className="btn btn-outline-secondary me-2" onClick={() => window.print()}>System Print</button>
          <Link to="/sis/students" className="btn btn-secondary">Back</Link>
        </div>
      </div>

      <div className="card p-3" ref={formRef}>
        <div className="depEd-header mb-3 avoid-break">
          <div className="depEd-header__left">
            <img
              src={(new URLSearchParams(window.location.search).get("seal") || "/deped_seal.png")}
              alt="DepEd Seal"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <div className="depEd-header__center">
            <div className="depEd-header__title">BASIC EDUCATION ENROLLMENT FORM</div>
            <div className="depEd-header__subtitle">THIS FORM IS NOT FOR SALE.</div>
          </div>
          <div className="depEd-header__right"></div>
        </div>
        <div className="mb-3 d-flex align-items-center gap-3 avoid-break">
          <img
            src={student.photoUrl || "/vmsc_logo.png"}
            alt="Student"
            className="student-photo"
            style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 8, border: "1px solid #ddd" }}
          />
          <div className="text-muted small">Photo</div>
        </div>
        <div className="form-header mb-3 avoid-break">
          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label small">School Year</label>
              <div className="boxed">{schoolYear}</div>
            </div>
            <div className="col-md-3">
              <label className="form-label small">Grade level to Enroll</label>
              <div className="boxed">{student.gradeLevel || ""}</div>
            </div>
            <div className="col-md-3">
              <label className="form-label small">With LRN?</label>
              <div className="checkline"><span className="box">{hasLRN ? "☑" : "☐"}</span> Yes <span className="box ms-3">{!hasLRN ? "☑" : "☐"}</span> No</div>
            </div>
            <div className="col-md-3">
              <label className="form-label small">Returning (Balik-Aral)?</label>
              <div className="checkline"><span className="box">{isReturning ? "☑" : "☐"}</span> Yes <span className="box ms-3">{!isReturning ? "☑" : "☐"}</span> No</div>
            </div>
          </div>
        </div>

        <h6 className="section-title">Learner Information</h6>
        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label small">PSA Birth Certificate No.</label>
            <div className="boxed"></div>
          </div>
          <div className="col-md-4">
            <label className="form-label small">Learner Reference No.</label>
            <div className="boxed">{student.lrn || ""}</div>
          </div>
          <div className="col-md-4">
            <label className="form-label small">Birthdate (mm/dd/yyyy)</label>
            <div className="boxed">{student.birthdate || student.birthDate || student.birth_date || ""}</div>
          </div>
          <div className="col-md-4">
            <label className="form-label small">(LRN) Last Name</label>
            <div className="boxed">{names.last}</div>
          </div>
          <div className="col-md-4">
            <label className="form-label small">First Name</label>
            <div className="boxed">{names.first}</div>
          </div>
          <div className="col-md-3">
            <label className="form-label small">Middle Name</label>
            <div className="boxed">{names.middle}</div>
          </div>
          <div className="col-md-1">
            <label className="form-label small">Ext.</label>
            <div className="boxed">{names.ext}</div>
          </div>
          <div className="col-md-4">
            <label className="form-label small">Place of Birth (Municipality/City)</label>
            <div className="boxed">{student.birthPlaceCity || ""}</div>
          </div>
          <div className="col-md-2">
            <label className="form-label small">Sex</label>
            <div className="checkline"><span className="box">{sex.startsWith("m") ? "☑" : "☐"}</span> Male <span className="box ms-3">{sex.startsWith("f") ? "☑" : "☐"}</span> Female</div>
          </div>
          <div className="col-md-2">
            <label className="form-label small">Age</label>
            <div className="boxed"></div>
          </div>
          <div className="col-md-4">
            <label className="form-label small">Mother Tongue</label>
            <div className="boxed">{student.motherTongue || ""}</div>
          </div>
        </div>
        <div className="row g-2 mt-2">
          <div className="col-md-4">
            <label className="form-label small">Student Email</label>
            <div className="boxed">{student.studentEmail || ""}</div>
          </div>
        </div>
        <div className="row g-2 mt-3">
          <div className="col-12">
            <div className="fw-bold">Current Address</div>
          </div>
          <div className="col-md-2"><label className="form-label small">House No.</label><div className="boxed">{student.currentHouse || ""}</div></div>
          <div className="col-md-3"><label className="form-label small">Sitio/Street Name</label><div className="boxed">{student.currentStreet || ""}</div></div>
          <div className="col-md-2"><label className="form-label small">Barangay</label><div className="boxed">{student.currentBarangay || ""}</div></div>
          <div className="col-md-3"><label className="form-label small">Municipality/City</label><div className="boxed">{student.currentCity || ""}</div></div>
          <div className="col-md-2"><label className="form-label small">Province</label><div className="boxed">{student.currentProvince || ""}</div></div>
          <div className="col-md-2"><label className="form-label small">Country</label><div className="boxed">{student.currentCountry || "Philippines"}</div></div>
          <div className="col-md-2"><label className="form-label small">Zip Code</label><div className="boxed">{student.currentZip || ""}</div></div>
        </div>
        <div className="row g-2 mt-3">
          <div className="col-12">
            <div className="fw-bold">Permanent Address</div>
          </div>
          <div className="col-12"><div className="small">Same with Current Address? {student.samePermanentAsCurrent ? "Yes" : "No"}</div></div>
          <div className="col-md-2"><label className="form-label small">House No./Street</label><div className="boxed">{student.samePermanentAsCurrent ? (student.currentHouse || "") : (student.permanentHouse || "")}</div></div>
          <div className="col-md-3"><label className="form-label small">Street Name</label><div className="boxed">{student.samePermanentAsCurrent ? (student.currentStreet || "") : (student.permanentStreet || "")}</div></div>
          <div className="col-md-2"><label className="form-label small">Barangay</label><div className="boxed">{student.samePermanentAsCurrent ? (student.currentBarangay || "") : (student.permanentBarangay || "")}</div></div>
          <div className="col-md-3"><label className="form-label small">Municipality/City</label><div className="boxed">{student.samePermanentAsCurrent ? (student.currentCity || "") : (student.permanentCity || "")}</div></div>
          <div className="col-md-2"><label className="form-label small">Province</label><div className="boxed">{student.samePermanentAsCurrent ? (student.currentProvince || "") : (student.permanentProvince || "")}</div></div>
          <div className="col-md-2"><label className="form-label small">Country</label><div className="boxed">{student.samePermanentAsCurrent ? (student.currentCountry || "Philippines") : (student.permanentCountry || "Philippines")}</div></div>
          <div className="col-md-2"><label className="form-label small">Zip Code</label><div className="boxed">{student.samePermanentAsCurrent ? (student.currentZip || "") : (student.permanentZip || "")}</div></div>
        </div>

        <div className="row g-2 mt-2">
          <div className="col-md-6">
            <label className="form-label small">Belonging to any Indigenous Peoples (IP) Community/Indigenous Cultural Community</label>
            <div className="checkline"><span className="box">☐</span> Yes <span className="box ms-3">☐</span> No <span className="ms-3">If Yes, please specify:</span> <span className="boxed flex-grow-1">{student.ipCommunity || ""}</span></div>
          </div>
          <div className="col-md-6">
            <label className="form-label small">Is your family a beneficiary of 4Ps?</label>
            <div className="checkline"><span className="box">{student.fourPsId ? "☑" : "☐"}</span> Yes <span className="box ms-3">{student.fourPsId ? "☐" : "☑"}</span> No <span className="ms-3">If Yes, write 4Ps Household ID:</span> <span className="boxed flex-grow-1">{student.fourPsId || ""}</span></div>
          </div>
        </div>

        <div className="disability mt-3">
          <label className="form-label small">Is the child a Learner with Disability?</label>
          <div className="mb-1"><span className="box">{student.hasDisability ? "☑" : "☐"}</span> Yes <span className="box ms-3">{!student.hasDisability ? "☑" : "☐"}</span> No</div>
          <div className="grid">
            {["Visual Impairment","Hearing Impairment","Learning Disability","Intellectual Disability","Autism Spectrum Disorder","Emotional- Behavioral Disorder","Orthopedic/Physical Handicap","Speech/Language Disorder","Cerebral Palsy","Special Health Problem/ Chronic Disease","Multiple Disorder","Cancer"].map((d,i)=>(
              <div key={i}><span className="box">{Array.isArray(student.disabilities) && student.disabilities.includes(d) ? "☑" : "☐"}</span> {d}</div>
            ))}
          </div>
        </div>

        <div className="page-break"></div>

        <h6 className="section-title mt-3">Parent's / Guardian's Information</h6>
        <div className="row g-2">
          <div className="col-md-3"><label className="form-label small">Father's Last Name</label><div className="boxed">{parentParts.last}</div></div>
          <div className="col-md-3"><label className="form-label small">First Name</label><div className="boxed">{parentParts.first}</div></div>
          <div className="col-md-3"><label className="form-label small">Middle Name</label><div className="boxed">{parentParts.middle}</div></div>
          <div className="col-md-3"><label className="form-label small">Father Contact</label><div className="boxed">{student.fatherContact || student.parentContact || ""}</div></div>
          <div className="col-md-3"><label className="form-label small">Father Email</label><div className="boxed">{student.fatherEmail || student.parentEmail || ""}</div></div>
        </div>
        <div className="row g-2">
          <div className="col-md-3"><label className="form-label small">Mother's Maiden Last Name</label><div className="boxed"></div></div>
          <div className="col-md-3"><label className="form-label small">First Name</label><div className="boxed">{motherParts.first}</div></div>
          <div className="col-md-3"><label className="form-label small">Middle Name</label><div className="boxed">{motherParts.middle}</div></div>
          <div className="col-md-3"><label className="form-label small">Mother Contact</label><div className="boxed">{student.motherContact || ""}</div></div>
          <div className="col-md-3"><label className="form-label small">Mother Email</label><div className="boxed">{student.motherEmail || ""}</div></div>
        </div>
        <div className="row g-2">
          <div className="col-md-3"><label className="form-label small">Legal Guardian's Last Name</label><div className="boxed">{guardianParts.last}</div></div>
          <div className="col-md-3"><label className="form-label small">First Name</label><div className="boxed">{guardianParts.first}</div></div>
          <div className="col-md-3"><label className="form-label small">Middle Name</label><div className="boxed">{guardianParts.middle}</div></div>
          <div className="col-md-3"><label className="form-label small">Contact Number</label><div className="boxed">{student.guardianContact || ""}</div></div>
          <div className="col-md-3"><label className="form-label small">Guardian Email</label><div className="boxed">{student.guardianEmail || ""}</div></div>
        </div>

        <h6 className="section-title mt-3">For Returning Learner / Transfer</h6>
        <div className="row g-2">
          <div className="col-md-4"><label className="form-label small">Last Grade Level Completed</label><div className="boxed"></div></div>
          <div className="col-md-4"><label className="form-label small">Last School Year Completed</label><div className="boxed"></div></div>
          <div className="col-md-4"><label className="form-label small">Last School Attended</label><div className="boxed"></div></div>
        </div>

        <h6 className="section-title mt-3">For Learners in Senior High School</h6>
        <div className="row g-2">
          <div className="col-md-3">
            <label className="form-label small">Semester</label>
            <div className="checkline"><span className="box">{["1","1st","first"].includes(shsSemester) ? "☑" : "☐"}</span> 1st <span className="box ms-3">{["2","2nd","second"].includes(shsSemester) ? "☑" : "☐"}</span> 2nd</div>
          </div>
          <div className="col-md-3"><label className="form-label small">Track</label><div className="boxed"></div></div>
          <div className="col-md-6"><label className="form-label small">Strand</label><div className="boxed">{student.strand || ""}</div></div>
        </div>

        <h6 className="section-title mt-3">Distance Learning Preferences</h6>
        <div className="grid">
          {["Modular (Print)","Modular (Digital)","Online","Educational Television","Radio-Based Instruction","Homeschooling","Blended"].map((d,i)=>(
            <div key={i}><span className="box">{dlSelected.has(String(d).toLowerCase()) ? "☑" : "☐"}</span> {d}</div>
          ))}
        </div>

        <div className="mt-4">
          <p className="small text-muted">
            I hereby certify that the above information given are true and correct to the best of my knowledge and I allow the Department of Education to use my child's details to create and/or update his/her learner profile in the Learner Information System. The information herein shall be treated as confidential in compliance with the Data Privacy Act of 2012.
          </p>
          <div className="row g-2">
            <div className="col-md-6">
              <label className="form-label small">Signature Over Printed Name of Parent/Guardian</label>
              <div className="boxed" style={{ height: 48 }}></div>
            </div>
            <div className="col-md-3">
              <label className="form-label small">Date</label>
              <div className="boxed"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
